import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import config from '../../../config';
import { loadComplete } from '../../utils/scripts';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';


class SecureDocumentsSharing extends Component {
  static documentSharingWidget;
  static propTypes = {
    bookingID: PropTypes.string.isRequired,
    assignedBankers: PropTypes.oneOfType([PropTypes.array]),
    assignedBankCasesCode: PropTypes.oneOfType([PropTypes.array]),
    assignedLawyers: PropTypes.oneOfType([PropTypes.array]),
    assignedLawyerCasesID: PropTypes.oneOfType([PropTypes.array]),
    loanTrackerLeadCode: PropTypes.string,
    // CPI-976: Vietnam gets the "Signed sales form (wet sign)" document type (upload + list).
    isVietnam: PropTypes.bool,
    t: PropTypes.func,
  };
  static defaultProps = {
    isVietnam: false,
  };
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      loaded: false,
    };
  }
  async componentDidMount() {
    await this.loadScript();
    this.renderWidget(
      this.props.bookingID,
      this.props.assignedBankers,
      this.props.assignedBankCasesCode,
      this.props.assignedLawyers,
      this.props.assignedLawyerCasesID,
      this.props.loanTrackerLeadCode,
      this.props.isVietnam);
  }
  async componentWillReceiveProps(nextProps) {
    if (JSON.stringify(this.props) !== JSON.stringify(nextProps)) {
      this.renderWidget(
        nextProps.bookingID,
        nextProps.assignedBankers,
        nextProps.assignedBankCasesCode,
        nextProps.assignedLawyers,
        nextProps.assignedLawyerCasesID,
        nextProps.loanTrackerLeadCode,
        nextProps.isVietnam);
    }
  }
  setStateAsync = state =>
    new Promise((res) => {
      this.setState(state, res);
    });
  async loadScript() {
    const url = config.get('secureDocumentsSharingWidgetURL');
    if (!url) {
      return;
    }
    await this.setStateAsync({ loading: true });
    try {
      await loadComplete(url);
    } catch (err) {
      Bugsnag.notifyException(err);
      await this.setStateAsync({ loading: false, loaded: false });
      return;
    }
    await this.setStateAsync({ loading: false, loaded: true });
  }
  renderWidget(
    bookingID,
    bankers,
    bankCasesCode,
    lawyers,
    lawyerCasesID,
    loanTrackerLeadCode,
    isVietnam = false) {
    const { t } = this.props;
    const paths = [];
    // CPI-976 (G3-D2): the wet-signed sales form is the Booked gate's evidence. Vietnam only.
    const vnDocumentTypes = isVietnam ? [
      {
        label: t('Signed Sales Form (Wet Sign)'),
        value: 'signed_sales_form_wet',
      },
    ] : [];
    if (bookingID) {
      paths.push(`showroom/bookings/${bookingID}`);
      if (bankCasesCode && bankCasesCode.length > 0) {
        bankCasesCode.forEach((caseCode) => {
          if (caseCode) {
            paths.push(`banker/cases/${caseCode}`);
          }
        });
      }
      if (lawyerCasesID && lawyerCasesID.length > 0) {
        lawyerCasesID.forEach((caseID) => {
          if (caseID) {
            paths.push(`lawyer/cases/${caseID}`);
          }
        });
      }
      // CPI-1249: was `!== ''`, so an undefined code asked the API about a deal that cannot exist.
      if (loanTrackerLeadCode) {
        paths.push(`loan-tracker/deals/${loanTrackerLeadCode}`);
      }

      // CPI-1249: in Vietnam this widget keeps one job, the wet-signed form; empty off Vietnam.
      const myUploadDocumentTypes = [
        {
          label: 'Bank Statement',
          value: 'bank_statement',
        },
        {
          label: 'CTOS Consent',
          value: 'ctos_consent',
        },
        {
          label: 'Declaration Form',
          value: 'declaration_form',
        },
        {
          label: 'EA Form',
          value: 'ea_form',
        },
        {
          label: 'IC',
          value: 'ic',
        },
        {
          label: 'Other Documents',
          value: 'other_documents',
        },
        {
          label: 'Pay Slip',
          value: 'pay_slip',
        },
        {
          label: 'Rebate Form',
          value: 'rebate_form',
        },
        {
          label: 'Title Details',
          value: 'title_details',
        },
      ];

      const w = new window.SecureDocumentsWidget({
        uploadDocumentTypes: isVietnam ? vnDocumentTypes : myUploadDocumentTypes,
        documentTypes: [
          {
            label: 'Bank Statement',
            value: 'bank_statement',
          },
          {
            label: 'CTOS Consent',
            value: 'ctos_consent',
          },
          {
            label: 'Declaration Form',
            value: 'declaration_form',
          },
          {
            label: 'EA Form',
            value: 'ea_form',
          },
          {
            label: 'Letter of Offer',
            value: 'letter_of_offer',
          },
          {
            label: 'IC',
            value: 'ic',
          },
          {
            label: 'Other Documents',
            value: 'other_documents',
          },
          {
            label: 'Pay Slip',
            value: 'pay_slip',
          },
          {
            label: 'Rebate Form',
            value: 'rebate_form',
          },
          {
            label: 'Title Details',
            value: 'title_details',
          },
          {
            label: 'EPF Statement',
            value: 'epf_statement',
          },
          {
            label: 'Property Booking Form',
            value: 'property_booking_form',
          },
          {
            label: 'Signed SPA',
            value: 'signed_spa',
          },
          ...vnDocumentTypes,
        ],
        app: 'showroom',
        path: paths,
        uploadPath: `showroom/bookings/${bookingID}`,
        data: {
          showPurchasers: false,
          showBankers: true,
          showLawyers: true,
          bankers,
          lawyers,
        },
        limit: 5,
      });
      w.render(this.documentSharingWidget);
    }
  }
  renderContent() {
    const { loading, loaded } = this.state;
    const { t } = this.props;
    if (loading) {
      return t('Loading...', { ns: TRANSLATION_FILES.translation });
    }
    if (loaded) {
      return t('Secure documents loading...', { ns: TRANSLATION_FILES.translation });
    }
    return t('Unable to load secure documents.', { ns: TRANSLATION_FILES.translation });
  }
  render() {
    return (
      <div
        className="securedocuments-root"
        ref={(ref) => {
          this.documentSharingWidget = ref;
        }}
      >
        {this.renderContent()}
      </div>
    );
  }
}

export default withTranslation(namespaces)(SecureDocumentsSharing);



// WEBPACK FOOTER //
// ./src/containers/Widgets/SecureDocumentsSharing.jsx